import { Input } from '@/components/ui/input';
import {
  useAddCandidateSkill,
  useRemoveCandidateSkill,
  useSetCandidateSkillVerification,
} from '@/hooks/use-candidates';
import { extractErrorMessage } from '@/lib/errors';
import { usePermission } from '@/lib/permissions';
import { isConsentLocked } from '../lib/consent-actions';
import type { CandidateDetail } from '@/types/candidates';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SkillChip } from './skill-chip';

interface CandidateSkillsCloudProps {
  candidate: CandidateDetail;
}

export function CandidateSkillsCloud({ candidate }: CandidateSkillsCloudProps) {
  // HW-1055: a candidate who revoked consent is read-only until anonymised.
  const canEdit = usePermission('recruitment.candidates.edit') && !isConsentLocked(candidate);
  const addSkill = useAddCandidateSkill(candidate.id);
  const toggleVerification = useSetCandidateSkillVerification(candidate.id);
  const removeSkill = useRemoveCandidateSkill(candidate.id);
  const [draft, setDraft] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Disabling the input while the mutation is in flight blurs it natively.
  // After the mutation settles we restore focus so recruiters can keep typing
  // skills back-to-back without reaching for the mouse.
  const wantsRefocusRef = useRef(false);

  useEffect(() => {
    if (!addSkill.isPending && wantsRefocusRef.current) {
      wantsRefocusRef.current = false;
      inputRef.current?.focus();
    }
  }, [addSkill.isPending]);

  const { verifiedCount, unverifiedCount } = useMemo(() => {
    let v = 0;
    for (const s of candidate.skills) if (s.verified) v += 1;
    return { verifiedCount: v, unverifiedCount: candidate.skills.length - v };
  }, [candidate.skills]);

  const handleAdd = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setAddError(null);
    wantsRefocusRef.current = true;
    try {
      await addSkill.mutateAsync(trimmed);
      setDraft('');
    } catch (err: unknown) {
      setAddError(extractErrorMessage(err, 'Failed to add skill'));
    }
  };

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-medium">Skills</h2>
        <span className="text-xs text-muted-foreground">
          {verifiedCount} verified · {unverifiedCount} unverified
        </span>
      </div>

      {candidate.skills.length === 0 ? (
        <p className="mb-3 text-sm text-muted-foreground">No skills yet.</p>
      ) : (
        <ul className="mb-3 flex flex-wrap gap-2">
          {candidate.skills.map((skill) => (
            <SkillChip
              key={skill.id}
              skill={skill}
              canEdit={canEdit}
              onToggle={() =>
                toggleVerification.mutate({
                  skillLinkId: skill.id,
                  verified: !skill.verified,
                })
              }
              onRemove={() => removeSkill.mutate(skill.id)}
            />
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="space-y-1">
          <Input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleAdd();
              }
            }}
            onBlur={() => setAddError(null)}
            placeholder="Add skill (Enter)"
            disabled={addSkill.isPending}
            className="h-9 text-sm"
          />
          {addError ? <p className="text-xs text-destructive">{addError}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

